import { toObject, getSurveyPositions } from '@m1/geo-utils';
import { SURVEY_STYLES } from '@m1/ol-map';
import OlMap from '@m1/ol-map';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import * as turf from '@turf/turf';
import dayjs from 'dayjs';
import { Draw } from 'ol/interaction';
import { toLonLat } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import React, { useMemo } from 'react';
import { RiCircleLine } from 'react-icons/ri';
import { useDispatch, useSelector, useStore } from 'react-redux';

import actions from '@/actions';
import { CommandButton } from '@/components/ui/kit/Button';
import { SURVEY_DEFAULT_OPTIONS, SURVEY_AREA_MAX } from '@/config';
import useHandlerWithMoveCheck from '@/hooks/useHandlerWithMoveCheck';
import { NotifierService as notifier } from '@/libs/Notifier';

const DrawArea = () => {
  const dispatch = useDispatch();
  const mode = useSelector((state) => state.editor.mode);
  const store = useStore();
  const map = OlMap.getMap();
  const { handleWithMoveCheck } = useHandlerWithMoveCheck();

  const pressed = useMemo(() => {
    return mode === 'path/area';
  }, [mode]);

  useUpdateEffect(() => {
    if (!pressed) return;

    const interaction = new Draw({
      source: new VectorSource({ wrapX: false }),
      type: 'Polygon',
      freehand: true,
      style: SURVEY_STYLES,
    });
    interaction.on('drawend', drawArea);
    map.addInteraction(interaction);

    return () => {
      map.removeInteraction(interaction);
    };
  }, [pressed]);

  const drawArea = (e) => {
    // 모든 좌표
    const allCoords = e.feature.getGeometry().getCoordinates()[0];
    // 중복 제거된 좌표
    const coords = allCoords.filter((coord, index) => {
      const curr = JSON.stringify(coord);
      const next = JSON.stringify(allCoords[index + 1]);
      return curr !== next;
    });
    const polygon = turf.polygon([coords.map((coord) => toLonLat(coord))]);

    const selfIntersections = turf.kinks(polygon).features;
    // 자기교차점 존재 시
    if (selfIntersections.length > 0) {
      notifier.error('The drawn area cannot have self-intersections. Please redraw.');
      return;
    }

    const area = turf.area(polygon);
    // 면적 200km² 초과 시
    if (SURVEY_AREA_MAX < area) {
      notifier.error(`The drawn area cannot exceed ${SURVEY_AREA_MAX / (10 ^ 6)}km². Please redraw.`);
      return;
    }

    const boundary = coords.map((coord) => {
      return toObject(toLonLat(coord));
    });
    const positions = getSurveyPositions(boundary, SURVEY_DEFAULT_OPTIONS);
    // 경로점 미존재 시
    if (positions.length === 0) {
      notifier.error('The area is too small. Please redraw.');
      return;
    }

    //! Logging for Sheco
    console.info('User finished drawing survey area at', dayjs().format('HH:mm:ss.SSS'));

    dispatch(actions.mission.appendSurvey(boundary, positions, 100));
    dispatch(actions.editor.changeMode(null));
  };

  const toggle = () => {
    if (store.getState().mission.json.commands.length === 0) {
      notifier.error('Please add a takeoff point first.');
      return;
    }

    if (pressed) {
      dispatch(actions.editor.changeMode(null));
    } else {
      dispatch(actions.editor.changeMode('path/area'));
    }
  };

  return <CommandButton Icon={RiCircleLine} text="Area" pressed={pressed} onClick={handleWithMoveCheck(toggle)} />;
};

export default DrawArea;
